/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from "@reduxjs/toolkit"

import { RootState } from "./store"
import { AccountDialogState, CLOSED } from "./types"

export interface PreferencesState {
    showPreferences: boolean
    showAccountSetUp: AccountDialogState
    navbarExpand: boolean
    sideBar: boolean
    showSearchFilters: boolean
    showDnoApplicationFilters: boolean
    showMFA: boolean
    theme: string
}

export const DARK = "dark"
export const LIGHT = "light"

const sideBarPref = localStorage.getItem("SideBarPref") === "true"

const prefersDarkMode =
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches

// Define initial state
const initialState: PreferencesState = {
    showPreferences: false,
    showAccountSetUp: CLOSED,
    navbarExpand: true,
    sideBar: sideBarPref,
    showSearchFilters: false,
    showDnoApplicationFilters: false,
    showMFA: false,
    theme: prefersDarkMode ? DARK : LIGHT,
}

export const preferencesSlice = createSlice({
    name: "preferences",
    // `createSlice` will infer the state type from the `initialState` argument
    initialState,
    reducers: {
        setShowPreferences: (state, action: PayloadAction<boolean>) => {
            state.showPreferences = action.payload
        },
        setShowAccountSetUp: (
            state,
            action: PayloadAction<AccountDialogState>
        ) => {
            state.showAccountSetUp = action.payload
        },
        setNavBarExpand: (state, action: PayloadAction<boolean>) => {
            state.navbarExpand = action.payload
        },
        setSideBar: (state, action: PayloadAction<boolean>) => {
            state.sideBar = action.payload
            localStorage.setItem("SideBarPref", action.payload.toString())
        },
        setShowSearchFilters: (state) => {
            state.showSearchFilters = !state.showSearchFilters
        },
        setShowDnoApplicationFilters: (state) => {
            state.showDnoApplicationFilters = !state.showDnoApplicationFilters
        },
        setShowMFA: (state, action: PayloadAction<boolean>) => {
            state.showMFA = action.payload
        },
        setTheme: (state, action: PayloadAction<string>) => {
            state.theme = action.payload
        },
    },
})

export const {
    setShowPreferences,
    setShowAccountSetUp,
    setNavBarExpand,
    setSideBar,
    setShowSearchFilters,
    setShowMFA,
    setShowDnoApplicationFilters,
    setTheme,
} = preferencesSlice.actions

// Helper functions for selecting state
export const getShowPreferences = (state: RootState): boolean =>
    state.preferences.showPreferences
export const getShowAccountSetUp = (state: RootState): AccountDialogState =>
    state.preferences.showAccountSetUp
export const getNavBarExpand = (state: RootState): boolean =>
    state.preferences.navbarExpand
export const getSideBar = (state: RootState): boolean =>
    state.preferences.sideBar
export const getShowSearchFilters = (state: RootState): boolean =>
    state.preferences.showSearchFilters
export const getShowDnoApplicationFilters = (state: RootState): boolean =>
    state.preferences.showDnoApplicationFilters
export const getShowMFA = (state: RootState): boolean =>
    state.preferences.showMFA
export const getTheme = (state: RootState): string => state.preferences.theme
export default preferencesSlice.reducer
